/* eslint-disable no-console -- Logger implementation intentionally writes to console. */
import { LogLevel, type Logger } from '@amplitude/analytics-types';

const LOGGER_PREFIX = 'Amplitude Logger ';

/**
 * Amplitude console errors that are expected in our multi-MFA portal: in-flight
 * analytics / G&S requests aborted on navigation, or SDK re-init during redirects.
 *
 * The bare browser fetch-failure wordings ("Failed to fetch", "NetworkError...",
 * "Load failed") are only treated as benign when they reference a rapidsos proxy
 * host in parens — that's the fingerprint of an in-flight request to our
 * /_amplitude proxy cancelled when the page navigates away. A hostless
 * "Failed to fetch" is left visible so a genuine proxy outage or sustained
 * connectivity issue still reaches Sentry. Auth/quota/decode errors that carry
 * real signal are likewise left unmatched.
 */
const BENIGN_AMPLITUDE_ERROR_PATTERNS: RegExp[] = [
  // Engagement (Guides & Surveys) + analytics remote-config context errors.
  /Failed to fetch decide data/i,
  /Failed to fetch remote end user store data/i,
  /Error fetching config\. Continuing with empty data/i,
  /Error fetching resource center settings\. Continuing with empty data/i,
  /Error decoding config response/i,
  /Error decoding project settings/i,
  /Failed to generate joined config/i,
  /Event rejected due to exceeded retry count/i,
  /Cannot unregister the default instance/i,
  /Engagement SDK not booted\. Ignoring identity change/i,
  // Navigation-abort network failures against our proxy host (Chrome/Edge,
  // Firefox, Safari wordings). Host-gated so a hostless failure stays visible.
  /Failed to fetch \([^)]*rapidsos[^)]*\)/i,
  /NetworkError when attempting to fetch resource\.\s*\([^)]*rapidsos[^)]*\)/i,
  /Load failed \([^)]*rapidsos[^)]*\)/i,
  /The operation was aborted/i,
];

export const isBenignAmplitudeLogMessage = (message: string): boolean =>
  BENIGN_AMPLITUDE_ERROR_PATTERNS.some(pattern => pattern.test(message));

const formatLogArgs = (args: unknown[]): string =>
  args
    .map(arg => {
      if (typeof arg === 'string') {
        return arg;
      }
      if (arg instanceof Error) {
        return arg.message;
      }
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');

/** Shared logger for analytics + engagement init; mirrors Amplitude's default formatting. */
export class FilteredAmplitudeLogger implements Logger {
  logLevel = LogLevel.Warn;

  disable(): void {
    this.logLevel = LogLevel.None;
  }

  enable(logLevel: LogLevel = LogLevel.Warn): void {
    this.logLevel = logLevel;
  }

  log(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Verbose) {
      return;
    }
    console.log(`${LOGGER_PREFIX}[Log]: ${formatLogArgs(args)}`);
  }

  warn(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Warn) {
      return;
    }
    console.warn(`${LOGGER_PREFIX}[Warn]: ${formatLogArgs(args)}`);
  }

  error(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Error) {
      return;
    }

    const message = formatLogArgs(args);
    if (isBenignAmplitudeLogMessage(message)) {
      return;
    }

    console.error(`${LOGGER_PREFIX}[Error]: ${message}`);
  }

  debug(...args: unknown[]): void {
    if (this.logLevel < LogLevel.Debug) {
      return;
    }
    console.log(`${LOGGER_PREFIX}[Debug]: ${formatLogArgs(args)}`);
  }
}

export const filteredAmplitudeLogger = new FilteredAmplitudeLogger();
